import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Music, Sparkles } from 'lucide-react';
import { getMediaShare, recordMediaView, type MediaShareView } from '../services/mediaShareService';

/**
 * Public viewer for a shared media asset (wisetree.ai/m/{mediaId}). The shared asset is shown in
 * FULL (never blurred — blurring the very thing a friend was linked to kills re-sharing); the
 * conversion gate is on the ACTION: "invite" shares add a join-the-chat CTA, view-only shares get a
 * soft "make your own" nudge. The join/accept flow is wired in the invite step.
 */
const MediaViewerPage: React.FC = () => {
  const { mediaId } = useParams<{ mediaId: string }>();
  const navigate = useNavigate();
  const [share, setShare] = useState<MediaShareView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!mediaId) return;
      try {
        const s = await getMediaShare(mediaId);
        if (alive) setShare(s);
      } catch (e: any) {
        if (alive) setError(e?.message || 'This share is not available.');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [mediaId]);

  // Count a view only after a short dwell (filters bounces/bots) — the scalable append path.
  useEffect(() => {
    if (!mediaId || loading || error) return;
    const t = setTimeout(() => { recordMediaView(mediaId); }, 3000);
    return () => clearTimeout(t);
  }, [mediaId, loading, error]);

  return (
    <div className="min-h-screen bg-secondary-950 text-white flex flex-col">
      <header className="flex items-center justify-between px-5 py-4">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm font-semibold tracking-tight">
          <Sparkles className="w-4 h-4" /> Wisetree
        </button>
        <button onClick={() => navigate('/')}
          className="rounded-full bg-white text-secondary-900 px-3.5 py-1.5 text-xs font-medium hover:bg-secondary-100">
          Make your own
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-4 pb-10">
        {loading && <Loader2 className="w-7 h-7 animate-spin text-secondary-400" />}
        {!loading && error && <p className="text-secondary-300 text-sm">{error}</p>}

        {!loading && share && (
          <div className="w-full max-w-3xl flex flex-col items-center gap-5">
            {share.kind === 'image' && (
              <img src={share.mediaUrl} alt={share.title}
                className="max-h-[72dvh] w-auto rounded-2xl object-contain shadow-2xl" />
            )}
            {share.kind === 'video' && (
              <video src={share.mediaUrl} controls autoPlay playsInline
                className="max-h-[72dvh] w-auto rounded-2xl shadow-2xl bg-black" />
            )}
            {share.kind === 'audio' && (
              <div className="w-full max-w-md rounded-2xl bg-secondary-900 border border-secondary-800 p-8 flex flex-col items-center gap-5">
                <div className="w-20 h-20 rounded-full bg-secondary-800 flex items-center justify-center">
                  <Music className="w-9 h-9 text-secondary-300" />
                </div>
                <audio src={share.mediaUrl} controls className="w-full" />
              </div>
            )}

            {share.mode === 'invite' ? (
              <div className="text-center">
                <p className="text-sm text-secondary-300">Join this chat to create your own.</p>
                <button onClick={() => navigate('/')}
                  className="mt-2 rounded-full bg-white text-secondary-900 px-5 py-2 text-sm font-medium hover:bg-secondary-100">
                  Accept invite &amp; join
                </button>
              </div>
            ) : (
              <p className="text-xs text-secondary-400">Made on Wisetree.</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default MediaViewerPage;
